import type { CSSProperties, ReactNode } from 'react'

export const BOOT_MS = 4200

const LINES: { text: string; blank?: boolean }[] = [
    { text: 'A fatal exception 0E has occurred at 0028:C0011E36 in VXD' },
    { text: 'DESK95(01) + 00010E36.  The current application will be' },
    { text: 'terminated.' },
    { text: '', blank: true },
    { text: '*  Press any key to terminate the current application.' },
    { text: '*  Press CTRL+ALT+DEL again to restart your computer. You' },
    { text: '   will lose any unsaved information in all open' },
    { text: '   applications.' }
]

/**
 * Deterministic scatter, generated once at module scope so the server and the
 * client render the exact same rectangles.
 */
const BLOCKS = Array.from({ length: 44 }, (_, index) => {
    const seed = (index * 2654435761) % 4294967296
    const a = (seed % 1000) / 1000
    const b = ((seed >> 7) % 1000) / 1000
    const c = ((seed >> 13) % 1000) / 1000
    const d = ((seed >> 19) % 1000) / 1000

    return {
        left: `${Math.round(a * 88)}%`,
        top: `${Math.round(b * 92)}%`,
        width: `${8 + Math.round(c * 34)}%`,
        height: `${2 + Math.round(d * 9)}%`,
        delay: 2300 + index * 22
    }
})

const mono: CSSProperties = {
    fontFamily: "'Lucida Console', 'Courier New', monospace",
    color: '#ffffff'
}

const lineStyle = (index: number): CSSProperties => ({
    ...mono,
    opacity: 0,
    animation: `desk95-line-in 1ms steps(1) ${120 + index * 160}ms forwards`
})

export const BootScreen = (): ReactNode => {
    return (
        <div
            className='absolute inset-0 overflow-hidden'
            style={{
                background: '#0a0ad6',
                zIndex: 9500,
                animation: `desk95-boot-out 480ms steps(3) ${BOOT_MS - 500}ms forwards`
            }}
            role='img'
            aria-label='Desk 95 boot screen: a Windows stop error resolving into the desktop'
        >
            <div
                className='absolute top-[6%] right-[3%] bottom-[6%] w-[38%] max-w-[360px] min-w-[150px]'
                style={{ opacity: 0, animation: 'desk95-figure-in 520ms ease-out 700ms forwards, desk95-figure-out 420ms ease-in 3000ms forwards' }}
                aria-hidden='true'
            >
                <div
                    className='relative size-full'
                    style={{ animation: 'desk95-glitch-shift 420ms steps(2) 1500ms 2' }}
                >
                    <img
                        src='/desk-95/boot-figure.png'
                        alt=''
                        className='absolute inset-0 size-full object-contain object-bottom'
                        style={{ filter: 'grayscale(1) contrast(1.6)' }}
                        draggable={false}
                    />

                    {BLOCKS.map((block, index) => (
                        <span
                            key={index}
                            className='absolute block'
                            style={{
                                left: block.left,
                                top: block.top,
                                width: block.width,
                                height: block.height,
                                background: '#0a0ad6',
                                opacity: 0,
                                animation: `desk95-block-in 1ms steps(1) ${block.delay}ms forwards`
                            }}
                        />
                    ))}
                </div>
            </div>

            <div className='relative flex size-full flex-col justify-center px-[6%] py-[8%] text-[clamp(9px,1.5vw,14px)] leading-[1.5]'>
                <div
                    className='mb-[1.5em] self-center px-[1em] py-[1px]'
                    style={{ ...mono, background: '#c0c0c0', color: '#0a0ad6', opacity: 0, animation: 'desk95-line-in 1ms steps(1) 60ms forwards' }}
                >
                    Windows
                </div>

                {LINES.map((line, index) => (
                    <p key={index} className='whitespace-pre' style={lineStyle(index)}>
                        {line.blank ? ' ' : line.text}
                    </p>
                ))}

                <p className='mt-[1.5em] whitespace-pre' style={lineStyle(LINES.length + 1)}>
                    Press any key to continue
                    <span
                        className='ml-[0.5em] inline-block h-[1em] w-[0.6em] translate-y-[0.15em] bg-white'
                        style={{ animation: 'desk95-blink 900ms steps(1) infinite' }}
                    />
                </p>
            </div>
        </div>
    )
}
